<script setup lang="ts">
import {
  computed, nextTick, onUnmounted, ref,
} from 'vue';

interface Props {
  content?: string;
  disabled?: boolean;
  placement?: 'bottom' | 'left' | 'right' | 'top';
}

const props = withDefaults(defineProps<Props>(), {
  content: '',
  disabled: false,
  placement: 'top',
});

const TOOLTIP_GAP = 8;
const VIEWPORT_PADDING = 12;
const TOOLTIP_Z_INDEX = 1100;

const isVisible = ref(false);
const triggerRef = ref<HTMLElement | null>(null);
const tooltipRef = ref<HTMLElement | null>(null);
const tooltipStyle = ref<Record<string, string>>({});

const placementClasses = computed(() => {
  const placements = {
    bottom: 'tooltip-bottom',
    left: 'tooltip-left',
    right: 'tooltip-right',
    top: 'tooltip-top',
  };
  return placements[props.placement];
});

const updatePosition = () => {
  const trigger = triggerRef.value;
  const tooltip = tooltipRef.value;
  if (!trigger || !tooltip) return;

  const rect = trigger.getBoundingClientRect();
  const tooltipRect = tooltip.getBoundingClientRect();
  const { innerHeight, innerWidth } = window;

  let top = 0;
  let left = 0;

  switch (props.placement) {
    case 'bottom':
      top = rect.bottom + TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tooltipRect.width / 2;
      break;
    case 'left':
      top = rect.top + rect.height / 2 - tooltipRect.height / 2;
      left = rect.left - tooltipRect.width - TOOLTIP_GAP;
      break;
    case 'right':
      top = rect.top + rect.height / 2 - tooltipRect.height / 2;
      left = rect.right + TOOLTIP_GAP;
      break;
    default:
      top = rect.top - tooltipRect.height - TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tooltipRect.width / 2;
      break;
  }

  const maxLeft = innerWidth - tooltipRect.width - VIEWPORT_PADDING;
  const maxTop = innerHeight - tooltipRect.height - VIEWPORT_PADDING;

  left = Math.min(Math.max(left, VIEWPORT_PADDING), Math.max(maxLeft, VIEWPORT_PADDING));
  top = Math.min(Math.max(top, VIEWPORT_PADDING), Math.max(maxTop, VIEWPORT_PADDING));

  tooltipStyle.value = {
    left: `${left}px`,
    top: `${top}px`,
    zIndex: String(TOOLTIP_Z_INDEX),
  };
};

const showTooltip = async () => {
  if (!props.disabled && props.content) {
    isVisible.value = true;
    await nextTick();
    requestAnimationFrame(() => {
      updatePosition();
    });
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
  }
};

const hideTooltip = () => {
  isVisible.value = false;
  window.removeEventListener('scroll', updatePosition, true);
  window.removeEventListener('resize', updatePosition);
};

onUnmounted(() => {
  window.removeEventListener('scroll', updatePosition, true);
  window.removeEventListener('resize', updatePosition);
});
</script>

<template>
  <div
    ref="triggerRef"
    class="tooltip-wrapper"
    @mouseenter="showTooltip"
    @mouseleave="hideTooltip"
    @focus="showTooltip"
    @blur="hideTooltip"
  >
    <slot />
    <Teleport to="body">
      <Transition name="tooltip-fade">
        <div
          v-if="isVisible && content"
          ref="tooltipRef"
          class="tooltip-content tooltip-content--fixed"
          :class="placementClasses"
          :style="tooltipStyle"
          role="tooltip"
        >
          <div class="tooltip-arrow" />
          <div class="tooltip-text">{{ content }}</div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.tooltip-wrapper {
  position: relative;
  display: inline-flex;
}

.tooltip-content {
  pointer-events: none;

  &--fixed {
    position: fixed;
    width: max-content;
    max-width: min(20rem, calc(100vw - 1.5rem));
  }

  .tooltip-text {
    background: #1e293b;
    color: white;
    font-size: 0.8125rem;
    font-weight: 500;
    line-height: 1.4;
    padding: 0.625rem 0.875rem;
    border-radius: 0.5rem;
    box-shadow:
      0 10px 15px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
    white-space: normal;
    overflow-wrap: break-word;
    text-align: left;
    max-width: min(20rem, calc(100vw - 1.5rem));
  }

  .tooltip-arrow {
    position: absolute;
    width: 8px;
    height: 8px;
    background: #1e293b;
    transform: rotate(45deg);
  }

  &.tooltip-top .tooltip-arrow {
    bottom: -4px;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
  }

  &.tooltip-bottom .tooltip-arrow {
    top: -4px;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
  }

  &.tooltip-left .tooltip-arrow {
    right: -4px;
    top: 50%;
    transform: translateY(-50%) rotate(45deg);
  }

  &.tooltip-right .tooltip-arrow {
    left: -4px;
    top: 50%;
    transform: translateY(-50%) rotate(45deg);
  }
}

.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.tooltip-fade-enter-from,
.tooltip-fade-leave-to {
  opacity: 0;
}
</style>
